"use client";

import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/utils/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, Building, DollarSign, Settings } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom"; // Imported Link
import { Button } from "@/components/ui/button"; // Imported Button

const AdminDashboard = () => {
  const { isAdmin, isSuperAdmin, profile, company } = useAuth(); // Added isSuperAdmin and company

  if (!isAdmin && !isSuperAdmin) {
    return <div className="p-4 text-destructive">Access denied. Admins only.</div>;
  }

  const { data: stats, isLoading } = useQuery({
    queryKey: ["admin_stats", company?.id, isSuperAdmin],
    queryFn: async () => {
      let companiesQuery = supabase.from("companies").select("*", { count: 'exact', head: true });
      let usersQuery = supabase.from("profiles").select("*", { count: 'exact', head: true });
      let subscriptionsQuery = supabase.from("subscriptions").select("*", { count: 'exact', head: true });

      if (!isSuperAdmin) {
        if (!company?.id) return { companies: 0, users: 0, subscriptions: 0 };
        companiesQuery = companiesQuery.eq("id", company.id);
        usersQuery = usersQuery.eq("company_id", company.id);
        subscriptionsQuery = subscriptionsQuery.eq("company_id", company.id);
      }

      const [companiesRes, usersRes, subscriptionsRes] = await Promise.all([
        companiesQuery,
        usersQuery,
        subscriptionsQuery,
      ]);
      return {
        companies: companiesRes.count || 0,
        users: usersRes.count || 0,
        subscriptions: subscriptionsRes.count || 0,
      };
    },
  });

  const title = isSuperAdmin ? "Platform Admin Dashboard" : "Company Admin Dashboard";
  const subtitle = isSuperAdmin ? "Global platform overview" : `Your company: ${company?.name}`;

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-3xl font-bold mb-6">{title}</h1>
      <p className="text-muted-foreground mb-6">{subtitle}</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Companies</CardTitle>
            <Building className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.companies}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.users}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Subscriptions</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.subscriptions}</div>
          </CardContent>
        </Card>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {isAdmin && (
                <Button variant="outline" asChild className="w-full justify-start">
                  <Link to="/dashboard/profile">Manage Company Settings</Link>
                </Button>
              )}
              {isSuperAdmin && (
                <>
                  <Button variant="outline" asChild className="w-full justify-start">
                    <Link to="/dashboard/admin/users">Manage Users</Link>
                  </Button>
                  <Button variant="outline" asChild className="w-full justify-start">
                    <Link to="/dashboard/admin/companies">Manage Companies</Link>
                  </Button>
                  <Button variant="outline" asChild className="w-full justify-start">
                    <Link to="/dashboard/admin/billing">Billing Overview</Link>
                  </Button>
                  <Button variant="outline" asChild className="w-full justify-start">
                    <Link to="/dashboard/admin/analytics">View Analytics</Link>
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>System Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Supabase Connected</span>
                <Badge variant="default">Active</Badge>
              </div>
              <div className="flex justify-between">
                <span>Stripe Integrated</span>
                <Badge variant={isSuperAdmin ? "default" : "secondary"}>{isSuperAdmin ? "Configured" : "Company Only"}</Badge>
              </div>
              <div className="flex justify-between">
                <span>RLS Enabled</span>
                <Badge variant="default">Secure</Badge>
              </div>
              {isSuperAdmin && (
                <div className="flex justify-between">
                  <span>Platform Role</span>
                  <Badge variant="default">Superadmin</Badge>
                </div>
              )}
              {isAdmin && !isSuperAdmin && (
                <div className="flex justify-between">
                  <span>Company Role</span>
                  <Badge variant="secondary">Admin</Badge>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;